import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { FileCheck, CheckCircle2, Eye } from 'lucide-react';

const DueDiligenceSection = ({ 
  company, 
  activeDoc, 
  setActiveDoc, 
  handleDocumentPreview 
}) => {
  const docsRef = useRef(null);

  // Helper to handle scroll and set active index
  const handleScroll = (ref, setActive, items) => {
    if (!ref.current) return;
    const scrollLeft = ref.current.scrollLeft;
    const cardWidth = ref.current.offsetWidth * 0.8; // 80% width
    const idx = Math.round(scrollLeft / (cardWidth * 0.95));
    setActive(Math.max(0, Math.min(items - 1, idx)));
  };

  const uploadedDocuments = company.documents?.filter(doc => doc.uploaded) || [];

  if (uploadedDocuments.length === 0) {
    return null;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
        <FileCheck className="w-6 h-6 text-blue-500" /> Due Diligence
      </h2>
      <div className="relative">
        <div className="max-w-[700px] mx-auto w-full">
          <div
            ref={docsRef}
            className="flex overflow-x-auto gap-4 snap-x snap-mandatory w-full [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
            onScroll={() => handleScroll(docsRef, setActiveDoc, uploadedDocuments.length)}
          >
            {uploadedDocuments.map((doc, idx) => (
              <div
                key={idx}
                className="flex flex-col items-start gap-2 p-4 rounded-xl border snap-center flex-shrink-0 bg-green-50 border-green-200 hover:bg-green-100 hover:border-green-300 transition-all duration-200 cursor-pointer"
                style={{ minWidth: '80%', maxWidth: '420px' }}
                onClick={() => handleDocumentPreview(doc)}
              >
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <div className="font-medium text-gray-900">{doc.name}</div>
                <div className="text-xs text-gray-500">
                  Uploaded: {doc.fileName}
                </div>
                {doc.lastUpdated && (
                  <div className="text-xs text-gray-400">
                    Last updated: {new Date(doc.lastUpdated).toLocaleDateString()}
                  </div>
                )}
                {/* Preview hint */}
                <div className="flex items-center gap-1 text-xs text-blue-600 mt-2">
                  <Eye className="w-3 h-3" />
                  Click to preview
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Live Dot pagination for docs */}
        <div className="flex justify-center mt-4 gap-2">
          {uploadedDocuments.map((_, idx) => (
            <button
              key={idx}
              onClick={() => {
                setActiveDoc(idx);
                if (docsRef.current) {
                  const cardWidth = docsRef.current.offsetWidth * 0.8;
                  docsRef.current.scrollTo({ left: idx * cardWidth, behavior: 'smooth' });
                }
              }}
              className={`w-2 h-2 rounded-full inline-block transition-all duration-200 focus:outline-none ${activeDoc === idx ? 'bg-blue-500 scale-125' : 'bg-gray-300'}`}
              aria-label={`Show document ${idx + 1}`}
            ></button>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default DueDiligenceSection; 